import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Suspense,
  lazy,
} from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  NavLink,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import "./TestTimeline.css";
import darkModeIcon from "./darkBT.gif"; // darkBT.gif 이미지 파일을 가져옵니다.
import logoQWER from "./log_QWER.jpeg"; // log_QWER 이미지 파일을 가져옵니다.
import timelineProfile from "./timeline-profile.jpg";
import mascot from "./mascot.png";
import qwerLogoColor from "./qwer-logo.svg";
import endGif1 from "./end1.gif"; // end1.gif 이미지 파일을 가져옵니다.
import endGif2 from "./end2.gif"; // end2.gif 이미지 파일을 가져옵니다.
import endGif3 from "./end3.gif"; // end3.gif 이미지 파일을 가져옵니다.
import SnsPage from "./snsPage";
import FanCam from "./fanCams";
import Letter from "./letter";
import Broadcast from "./broadcast";
import NewBawige from "./newbawige";
import StreamingImageMerger from "./streamingImage";
import StreamingImageMergerLegacy from "./streamingImageLegacy";
import { Analytics } from "@vercel/analytics/react";
import { useTranslation } from "react-i18next";
import i18n from "../src/lang/i18n";
import TestCardList from "./test";

// 메인 헤드라인 문구 우선순위:
//   ① gist 의 flix_settings.json (flix-admin 페이지에서 배포 없이 변경)
//   ② Vercel 환경변수 REACT_APP_HOME_HEADLINE (재배포 필요)
//   ③ 아래 기본값
const DEFAULT_HEADLINE =
  (process.env.REACT_APP_HOME_HEADLINE || "").trim() || "CHOOSE YOUR QWER";

// 사이트 설정 JSON URL. 미설정 시 메인 목록 gist 와 같은 위치의 flix_settings.json 사용
const SITE_SETTINGS_URL = (() => {
  const explicit = (process.env.REACT_APP_SITE_SETTINGS_URL || "").trim();
  if (explicit) return explicit;
  const main = (process.env.REACT_APP_MAIN_DATA_URL || "").trim();
  if (!main) return "";
  return main.replace(/[^/]*$/, "flix_settings.json");
})();

const AdminApp = lazy(() => import("./admin/AdminApp"));

// 메인 목록 유형 필터. 값은 gist 항목의 type 필드와 같다.
const VIDEO_TYPES = [
  { key: "own", label: "typeOwn" },
  { key: "member", label: "typeMember" },
  { key: "stage", label: "typeStage" },
  { key: "guest", label: "typeGuest" },
  { key: "promo", label: "typePromo" },
];
// 유형 필터로 옮겨간 태그는 시기·시리즈 드롭다운에서 숨긴다
const TYPE_COVERED_EVENTS = ["none", "mv", "TV_Music"];

const extractYouTubeID = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const formatDate = (dateObject) => {
  if (dateObject && dateObject.$date) {
    return dateObject.$date.split("T")[0]; // YYYY-MM-DD 형식으로 자르기
  }
  return ""; // 날짜가 유효하지 않으면 빈 문자열 반환
};

const isMobile = () => {
  return /Mobi|Android|iPhone/i.test(navigator.userAgent);
};

const getRandomEndGif = () => {
  const gifs = [endGif1, endGif2, endGif3];
  return gifs[Math.floor(Math.random() * gifs.length)];
};

// 모바일 전용 하단 고정 탭바 (인스타 스타일). 데스크탑에선 CSS로 숨김.
// 어드민 라우트(/bawige-work)에선 메인 네비(햄버거/하단 탭바) 렌더 안 함
const HideOnAdmin = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/bawige-work")) return null;
  return children;
};

const BottomTabBar = ({ t }) => {
  const tabClass = ({ isActive }) => `bottom-tab ${isActive ? "active" : ""}`;
  return (
    <nav className="bottom-tab-bar" aria-label="하단 메뉴">
      <NavLink to="/newbawige" className={tabClass}>
        <span
          className="bottom-tab-mascot"
          style={{
            WebkitMaskImage: `url(${mascot})`,
            maskImage: `url(${mascot})`,
          }}
        />
        <span>{t("tabNewFan")}</span>
      </NavLink>
      <NavLink to="/sns" className={tabClass}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <circle cx="18" cy="5" r="2.5" />
          <circle cx="6" cy="12" r="2.5" />
          <circle cx="18" cy="19" r="2.5" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.2 10.8l7.6-4.6M8.2 13.2l7.6 4.6"
          />
        </svg>
        <span>{t("tabSNS")}</span>
      </NavLink>
      <NavLink to="/" end className={tabClass}>
        <img className="bottom-tab-img" src={qwerLogoColor} alt="" />
        <span>{t("tabHome")}</span>
      </NavLink>
      <NavLink to="/fancam" className={tabClass}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10 9.3l5 2.7-5 2.7z"
          />
        </svg>
        <span>{t("tabFanCam")}</span>
      </NavLink>
      <NavLink to="/broadcast" className={tabClass}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <rect x="6" y="3" width="12" height="14" rx="2" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 11h12M9 17l-2 3M15 17l2 3"
          />
        </svg>
        <span>{t("tabROKA")}</span>
      </NavLink>
    </nav>
  );
};

const App = () => {
  const [darkMode, setDarkMode] = useState(true); // 기본 설정을 다크 모드로 변경
  const [allItems, setAllItems] = useState([]);
  // 표시 개수만 state로 관리 — 실제 표시 목록(items)은 아래에서 파생시킨다.
  const [visibleCount, setVisibleCount] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const loader = useRef(null);
  const scrollTimeout = useRef(null);
  const [endGif, setEndGif] = useState(getRandomEndGif());
  const [sortOrder, setSortOrder] = useState("asc");
  const [menuOpen, setMenuOpen] = useState(false);
  const [eventList, setEventList] = useState([]);
  const [headline, setHeadline] = useState(DEFAULT_HEADLINE);
  // 헤드라인 아래 링크(선택). flix_settings.json 의 linkText / linkUrl
  const [homeLink, setHomeLink] = useState({ text: "", url: "" });
  const [selectedEvent, setSelectedEvent] = useState("");
  const [isEventListOpen, setIsEventListOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("");
  const typeChipsRef = useRef(null);
  const setIndexA = [1, 2];
  const { t } = useTranslation();


  const handleNavItemClick = (event) => {
    const navItems = document.querySelectorAll(".nav-item");
    navItems.forEach((item) => {
      if (item !== event.currentTarget) {
        item.classList.remove("active");
      }
    });
    event.currentTarget.classList.toggle("active");
  };
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    const hamburger = document.querySelector(".hamburger");
    hamburger.classList.toggle("active");
  };
  const closeMenu = () => {
    setMenuOpen(false);
    const hamburger = document.querySelector(".hamburger");
    hamburger.classList.remove("active");
  };
  const itemsPerPage = 15;
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };
  const toggleEventList = () => {
    setIsEventListOpen(!isEventListOpen);
  };
  // 현재 정렬/필터 기준으로 allItems를 정렬·필터링한 "전체 목록" 반환.
  // 표시 목록(items)은 항상 이 결과의 앞에서부터 slice 하므로,
  // append 누적으로 인한 중복/뒤섞임이 발생하지 않는다.
  const matchesEvent = (item, event) =>
    !event || (item.tag && item.tag.includes(event));
  const getSortedFiltered = (order = sortOrder, event = selectedEvent) => {
    const filtered = allItems.filter(
      (item) =>
        matchesEvent(item, event) &&
        (!selectedType || item.type === selectedType) &&
        (!selectedChannel || item.channel === selectedChannel),
    );
    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.uploadDate.$date);
      const dateB = new Date(b.uploadDate.$date);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  // 표시 목록은 매 렌더마다 파생 — state 불일치/append 중복이 발생할 수 없다.
  const sortedFilteredItems = getSortedFiltered();
  const items = sortedFilteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < sortedFilteredItems.length;

  // 칩 숫자는 시기·시리즈 선택을 반영한 개수
  const eventItems = allItems.filter((item) => matchesEvent(item, selectedEvent));
  const typeCounts = eventItems.reduce((counts, item) => {
    counts[item.type] = (counts[item.type] || 0) + 1;
    return counts;
  }, {});
  const memberChannels = Object.entries(
    eventItems
      .filter((item) => item.type === "member" && item.channel)
      .reduce((counts, item) => {
        counts[item.channel] = (counts[item.channel] || 0) + 1;
        return counts;
      }, {}),
  ).sort((a, b) => b[1] - a[1]);
  const hasActiveFilter = Boolean(
    selectedEvent || selectedType || selectedChannel,
  );

  const loadMoreItems = async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      // 모바일에서는 더 짧은 지연 시간 사용
      const delay = isMobile() ? 150 : 300;
      await new Promise((resolve) => setTimeout(resolve, delay));

      // 함수형 업데이트라 stale 클로저에도 안전
      setVisibleCount((c) => c + itemsPerPage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = () => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // 모바일에서는 더 짧은 디바운스 시간 사용
    const debounceTime = isMobile() ? 100 : 150;

    scrollTimeout.current = setTimeout(() => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      let scrollThreshold;
      if (isMobile()) {
        scrollThreshold = documentHeight * 0.5; // 모바일은 50% 지점
      } else {
        scrollThreshold = documentHeight - windowHeight - 100; // 데스크탑은 기존 방식
      }

      if (scrollTop >= scrollThreshold) {
        loadMoreItems();
      }
    }, debounceTime);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [isLoading, items, hasMore]);

  useEffect(() => {
    // 원격 목록 URL(gist raw 등). 미설정 시 로컬 /data.json만 사용.
    const REMOTE_DATA_URL = process.env.REACT_APP_MAIN_DATA_URL;

    const fetchFrom = async (url) => {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Network response was not ok (${response.status})`);
      }
      const data = await response.json();
      // gist는 배열을 그대로 주지만, {record:[...]} 형태로 감싸져 와도 대응
      return Array.isArray(data) ? data : data.record;
    };

    const fetchData = async () => {
      setIsLoading(true);
      let data = null;

      // ① 원격(gist) 먼저 시도
      if (REMOTE_DATA_URL) {
        try {
          data = await fetchFrom(REMOTE_DATA_URL);
        } catch (error) {
          console.warn("원격 목록 로드 실패, 로컬 폴백 사용:", error);
        }
      }

      // ② 실패하거나 URL 미설정 시 로컬 /data.json 폴백
      if (!data) {
        try {
          data = await fetchFrom("/data.json");
        } catch (error) {
          console.error("Error loading JSON data:", error);
        }
      }

      if (data) {
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.uploadDate.$date);
          const dateB = new Date(b.uploadDate.$date);
          return dateA - dateB;
        });
        setAllItems(sortedData);
        setVisibleCount(itemsPerPage);
      }

      setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    // 원격 사이트 설정(헤드라인 등). 실패하거나 값이 비어 있으면 기본값 유지
    if (!SITE_SETTINGS_URL) return;
    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const response = await fetch(SITE_SETTINGS_URL, { cache: "no-store" });
        if (!response.ok) return;
        const data = await response.json();
        if (!data || cancelled) return;
        const remote =
          typeof data.headline === "string" ? data.headline.trim() : "";
        if (remote) setHeadline(remote);
        const url = typeof data.linkUrl === "string" ? data.linkUrl.trim() : "";
        const text = typeof data.linkText === "string" ? data.linkText.trim() : "";
        // linkEnabled 가 명시적으로 false 면 숨김. 값이 없으면(예전 저장분) 표시
        const enabled = data.linkEnabled !== false;
        if (enabled && /^https?:\/\/\S+$/i.test(url)) setHomeLink({ text, url });
      } catch (error) {
        console.warn("사이트 설정 로드 실패, 기본 헤드라인 사용:", error);
      }
    };
    fetchSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchEventList = async () => {
      try {
        const response = await fetch("/eplist.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setEventList(data);
      } catch (error) {
        console.error("Error loading event list:", error);
      }
    };
    fetchEventList();
  }, []);

  const getYouTubeAppLink = (url) => {
    const videoId = extractYouTubeID(url);
    if (!videoId) return url;
    if (/Android/i.test(navigator.userAgent)) {
      return `intent://www.youtube.com/watch?v=${videoId}#Intent;package=com.google.android.youtube;scheme=https;end`;
    }
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const handleThumbnailClick = (url) => {
    const link = isMobile() ? getYouTubeAppLink(url) : url;
    window.open(link, "_blank");
  };

  const handleEventFilter = (event) => {
    const newEvent = event === selectedEvent ? null : event;
    const nextItems = allItems.filter((item) => matchesEvent(item, newEvent));
    if (selectedType && !nextItems.some((item) => item.type === selectedType)) {
      setSelectedType("");
      setSelectedChannel("");
    } else if (
      selectedChannel &&
      !nextItems.some((item) => item.channel === selectedChannel)
    ) {
      setSelectedChannel("");
    }
    setSelectedEvent(newEvent);
    setIsEventListOpen(false);
    setVisibleCount(itemsPerPage);
  };

  const handleTypeFilter = (type) => {
    setSelectedType(type);
    setSelectedChannel("");
    setVisibleCount(itemsPerPage);
  };

  const handleChannelFilter = (channel) => {
    setSelectedChannel(channel);
    setVisibleCount(itemsPerPage);
  };

  const resetFilters = () => {
    setSelectedEvent("");
    setSelectedType("");
    setSelectedChannel("");
    setVisibleCount(itemsPerPage);
  };

  // 모바일 가로 스크롤에서 선택한 칩이 잘리지 않게 가운데로 옮긴다
  useEffect(() => {
    const container = typeChipsRef.current;
    const chip = container && container.querySelector(".type-chip.selected");
    if (!chip) return;
    container.scrollTo({
      left: chip.offsetLeft - (container.clientWidth - chip.offsetWidth) / 2,
      behavior: "smooth",
    });
  }, [selectedType]);

  const handleSort = () => {
    // 정렬 순서 토글 (setSortOrder 업데이터 안에서 setItems를 중첩 호출하면
    // 목록이 갱신되지 않으므로, 여기서 직접 계산해 각 state를 개별로 설정한다)
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    setVisibleCount(itemsPerPage);
    window.scrollTo({ top: 0 });
  };
  const onChangeLangKO = () => {
    i18n.changeLanguage("ko");
  };
  const onChangeLangJP = () => {
    i18n.changeLanguage("jp");
  };
  const onChangeLangENG = () => {
    i18n.changeLanguage("en");
  };

  const clearEventFilter = (e) => {
    e.stopPropagation(); // 이벤트 버블링 방지
    setSelectedEvent("");
    setVisibleCount(itemsPerPage);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 600) {
        setMenuOpen(false);
        const hamburger = document.querySelector(".hamburger");
        if (hamburger) hamburger.classList.remove("active");
        const navUl = document.querySelector(".navbar ul");
        if (navUl) {
          navUl.classList.remove("active");
          navUl.style.display = ""; // display 속성도 초기화
        }
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Router>
      <div className={`App ${darkMode ? "dark-mode" : "light-mode"}`}>
        <HideOnAdmin>
          <nav className="navbar">
            <button
              className="hamburger"
              onClick={toggleMenu}
              aria-label="메뉴"
            >
              <img className="hamburger-logo" src={qwerLogoColor} alt="메뉴" />
            </button>
            <div
              className={`menu-overlay ${menuOpen ? "active" : ""}`}
              onClick={closeMenu}
            />
            <div className={`side-menu ${menuOpen ? "active" : ""}`}>
              <button className="menu-close-btn" onClick={closeMenu}>
                &times;
              </button>
              <ul className={menuOpen ? "active" : ""}>
                <li>
                  <NavLink to="/" end onClick={closeMenu}>
                    {t("YouTube")}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/newbawige" onClick={closeMenu}>
                    {t("newFan")}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/sns" onClick={closeMenu}>
                    {t("SNS")}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/fancam" onClick={closeMenu}>
                    {t("FanCam")}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/broadcast" onClick={closeMenu}>
                    {t("ROKA")}
                  </NavLink>
                </li>
                <li>
                  <Link
                    to="https://www.qwerlives.com/"
                    target="_blank"
                    onClick={closeMenu}
                  >
                    QWER Lives
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      style={{ marginLeft: 4, flexShrink: 0 }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6m5-3h3m0 0v3m0-3L10 14"
                      />
                    </svg>
                  </Link>
                </li>
                <li
                  style={{
                    display: "flex",
                    alignItems:
                      typeof window !== "undefined" && window.innerWidth <= 600
                        ? "center"
                        : "center",
                    justifyContent:
                      typeof window !== "undefined" && window.innerWidth <= 600
                        ? "center"
                        : undefined,
                    flexDirection:
                      typeof window !== "undefined" && window.innerWidth <= 600
                        ? "column"
                        : "row",
                    width:
                      typeof window !== "undefined" && window.innerWidth <= 600
                        ? "100%"
                        : undefined,
                  }}
                >
                  <Link
                    to="https://bawige.net/library"
                    target="_blank"
                    onClick={closeMenu}
                  >
                    {t("BWGL")}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      style={{ marginLeft: 4, flexShrink: 0 }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6m5-3h3m0 0v3m0-3L10 14"
                      />
                    </svg>
                  </Link>
                </li>
                <li>
                  <a
                    href="https://dc-lottery.kr/youtube-search"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                  >
                    QWER 유튜브 대사 검색
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      style={{ marginLeft: 4, flexShrink: 0 }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6m5-3h3m0 0v3m0-3L10 14"
                      />
                    </svg>
                  </a>
                </li>
                <li>
                  <NavLink to="/images" onClick={closeMenu}>
                    스밍짤 만들기
                  </NavLink>
                </li>
              </ul>
            </div>
          </nav>
        </HideOnAdmin>
        <Routes>
          <Route
            exact
            path="/"
            element={
              <>
                <>
                  <h1>{t("YouTube")}</h1>
                  {/* 문구는 flix-admin 페이지(gist flix_settings.json)에서 변경. 없으면 환경변수 → 기본값 */}
                  {/* 메모 표출로 되돌리려면 아래 줄을 <h3>{t("devMemo")}</h3> 로 변경 */}
                  <h3>{headline}</h3>
                  {homeLink.url && (
                    <a
                      className="home-link"
                      href={homeLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {homeLink.text || homeLink.url}
                    </a>
                  )}
                  {/* <p>
                    {t("totVideos")}: {allItems.length}
                  </p> */}
                </>
                {/* <button
                  className="toggle-button"
                  onClick={() => setDarkMode(!darkMode)}
                >
                  <div className="bwicon"></div>
                  <span>{t("switMode")}</span>
                </button> */}

                <div class="lang-switch">
                  <button
                    className={`lang-btn ${
                      i18n.language === "ko" ? "active" : ""
                    }`}
                    onClick={onChangeLangKO}
                  >
                    KR
                  </button>
                  <button
                    className={`lang-btn ${
                      i18n.language === "en" ? "active" : ""
                    }`}
                    onClick={onChangeLangENG}
                  >
                    EN
                  </button>
                  <button
                    className={`lang-btn ${
                      i18n.language === "jp" ? "active" : ""
                    }`}
                    onClick={onChangeLangJP}
                  >
                    JP
                  </button>
                </div>

                <button className="sort-button" onClick={handleSort}>
                  <span className="control-desktop-label">{sortOrder === "asc" ? t("sortNew") : t("sortOld")}</span>
                  <span className="control-mobile-label">{t("sortLabel")}: {sortOrder === "asc" ? t("orderOld") : t("orderNew")}</span>
                </button>
                <div className="event-picker">
                  <div className="event-list-header" onClick={toggleEventList}>
                    <h3 className="event-list-title">
                      <span className="control-desktop-label">{selectedEvent ? t(selectedEvent) : t("filterTitle")}</span>
                      <span className="control-mobile-label">{t("categoryLabel")}: {selectedEvent ? t(selectedEvent) : t("categoryAll")}</span>
                    </h3>
                    <div className="header-controls">
                      {selectedEvent && (
                        <button
                          className="clear-filter"
                          onClick={clearEventFilter}
                          aria-label="Clear filter"
                        >
                          ×
                        </button>
                      )}
                      <span
                        className={`event-list-toggle ${
                          isEventListOpen ? "open" : ""
                        }`}
                      >
                        ▼
                      </span>
                    </div>
                  </div>
                  <div
                    className={`event-list-content ${
                      isEventListOpen ? "open" : ""
                    }`}
                  >
                    {eventList
                      .filter((event) => !TYPE_COVERED_EVENTS.includes(event))
                      .map((event, index) => (
                      <div
                        key={index}
                        className={`event-item ${
                          selectedEvent === event ? "selected" : ""
                        }`}
                        onClick={() => handleEventFilter(event)}
                      >
                        {t(event)}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="type-filters">
                  <div className="type-chips" ref={typeChipsRef}>
                    <button
                      className={`type-chip ${selectedType ? "" : "selected"}`}
                      onClick={() => handleTypeFilter("")}
                    >
                      {t("categoryAll")}
                      <span className="chip-count">{eventItems.length}</span>
                    </button>
                    {VIDEO_TYPES.map(({ key, label }) => (
                      <button
                        key={key}
                        className={`type-chip ${
                          selectedType === key ? "selected" : ""
                        }`}
                        disabled={!typeCounts[key]}
                        onClick={() => handleTypeFilter(key)}
                      >
                        {t(label)}
                        <span className="chip-count">{typeCounts[key] || 0}</span>
                      </button>
                    ))}
                  </div>
                  {selectedType === "member" && (
                    <div className="type-chips channel-chips">
                      <button
                        className={`channel-chip ${
                          selectedChannel ? "" : "selected"
                        }`}
                        onClick={() => handleChannelFilter("")}
                      >
                        {t("categoryAll")}
                        <span className="chip-count">
                          {typeCounts.member || 0}
                        </span>
                      </button>
                      {memberChannels.map(([channel, count]) => (
                        <button
                          key={channel}
                          className={`channel-chip ${
                            selectedChannel === channel ? "selected" : ""
                          }`}
                          onClick={() => handleChannelFilter(channel)}
                        >
                          {channel}
                          <span className="chip-count">{count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {hasActiveFilter && (
                    <div className="filter-result">
                      <button className="filter-reset" onClick={resetFilters}>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M13.013 3H2l8 9.46V19l4 2v-8.54l.9-1.055" />
                          <path d="m22 3-5 5" />
                          <path d="m17 3 5 5" />
                        </svg>
                        {t("resetFilters")}
                      </button>
                    </div>
                  )}
                </div>
                <div className="test-timeline">
                  {items.map((item, index) => {
                    const videoId = extractYouTubeID(item.url);
                    const thumbnailUrl = videoId
                      ? `https://img.youtube.com/vi/${videoId}/0.jpg`
                      : "";
                    const titleClass =
                      item.title.length > 10 ? "scrolling-title" : "";
                    return (
                      <div
                        className={`test-timeline-item ${
                          index % 2 === 0 ? "left" : "right"
                        }`}
                        key={item.url}
                      >
                        <div className="test-timeline-circle">
                          <img
                            src={timelineProfile}
                            alt="QWER Logo"
                            className="test-timeline-circle-img"
                          />
                        </div>
                        <div className="test-timeline-card">
                          {thumbnailUrl && (
                            <img
                              src={thumbnailUrl}
                              alt={item.title}
                              className="thumbnail"
                              onClick={() => handleThumbnailClick(item.url)}
                            />
                          )}
                          <div className="test-timeline-title-container">
                            <h2>{item.title}</h2>
                          </div>
                          <div className="test-timeline-date">
                            {formatDate(item.uploadDate)}
                            {item.channel && ` · ${item.channel}`}
                          </div>
                          <a
                            href={
                              isMobile()
                                ? getYouTubeAppLink(item.url)
                                : item.url
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="test-timeline-link"
                          >
                            {t("watch")}
                          </a>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={loader} className="loader">
                    {isLoading && (
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>{t("loading")}</p>
                      </div>
                    )}
                  </div>

                  <a> 🐣건의사항및 영상추가 요청은 ⬆ 클릭</a>
                  <Analytics />
                </div>
                <footer className="footer">{t("footer")}</footer>
              </>
            }
          ></Route>
          <Route
            path="/sns"
            element={
              <SnsPage
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                language={t}
                langbt={{
                  en: onChangeLangENG,
                  jp: onChangeLangJP,
                  ko: onChangeLangKO,
                }}
              />
            }
          />
          <Route
            path="/fancam"
            element={
              <FanCam
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                language={t}
                langbt={{
                  en: onChangeLangENG,
                  jp: onChangeLangJP,
                  ko: onChangeLangKO,
                }}
              />
            }
          />
          <Route
            path="/broadcast"
            element={
              <Broadcast
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                language={t}
                langbt={{
                  en: onChangeLangENG,
                  jp: onChangeLangJP,
                  ko: onChangeLangKO,
                }}
              />
            }
          />
          <Route
            path="/newbawige"
            element={
              <NewBawige
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                language={t}
                langbt={{
                  en: onChangeLangENG,
                  jp: onChangeLangJP,
                  ko: onChangeLangKO,
                }}
              />
            }
          />
          <Route
            path="/images"
            element={
              <StreamingImageMerger
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                language={t}
                langbt={{
                  en: onChangeLangENG,
                  jp: onChangeLangJP,
                  ko: onChangeLangKO,
                }}
              />
            }
          />
          <Route path="/images/legacy" element={<StreamingImageMergerLegacy />} />
          {/* <Route
            path="/letter"
            element={
              <TestCardList
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                language={t}
              />
            }
          /> */}
          <Route
            path="/bawige-work/*"
            element={
              <Suspense
                fallback={<div className="admin-loading">로딩 중...</div>}
              >
                <AdminApp />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* 사이드 메뉴 열렸을 땐 하단 탭바 숨김 (메뉴 항목 가림 방지) */}
        {!menuOpen && (
          <HideOnAdmin>
            <BottomTabBar t={t} />
          </HideOnAdmin>
        )}
      </div>
    </Router>
  );
};

export default App;
